import { Box, Paper, Typography } from '@mui/material';
import InlineGroup from '@shared/components/InlineGroup';
import MultilineGroup from '@shared/components/MultilineGroup';
import RoundedBox from '@shared/components/RoundedBox';
import { TextInputReadOnlyBox } from '@shared/components/TextInputReadOnlyBox';
import { useUserContext } from '@shared/contexts/UserContextProvider';
import { useScreenSize } from '@shared/hooks/useScreenSize';
import { chunkArray } from '@shared/utils/array';
import React, { useEffect, useState } from 'react';
import { EditButton } from './components/EditButton';
import { EditLadderLimitsDialog } from './components/EditLadderLimitsDialog';
import { EditTradeLimitsDialog } from './components/EditTradeLimitsDialog';
import { UserInfoList } from './components/UserInfoList';
import { Organization } from '@protos/organization';
import { User } from '@protos/user';
import { EditTradeAccountsDialog } from './components/EditTradeAccountsDialog';
import { EditPermissionsDialog } from './components/EditPermissionsDialog';
import { UserFilteredWebsockets } from './components/UserFilteredWebsockets';
import { checkIsABNUser, checkIsLadderUser, checkIsAdminReadOnlyUser, checkIsTradingUser } from '@utils/permissions';
import { useNavigate } from 'react-router-dom';
import { StyledIconButton } from './components/StyledIconButton';
import { tradeLimitsList } from './utils';

type Props = {
  user: User;
  orgData?: Organization;
};

const generateUserInfoAdmin = (user: User) => [
  {
    label: 'Id',
    value: user.id ? `${user.id}` : undefined,
  },
  {
    label: 'Full name',
    value: user.full_name ? `${user.full_name}` : undefined,
  },
  {
    label: 'Account name',
    value: user.account_name ? `${user.account_name}` : undefined,
  },
  {
    label: 'Email',
    value: user.email ? `${user.email}` : undefined,
  },
  {
    label: 'Whatsapp number',
    value: user.whatsapp_number ? `${user.whatsapp_number}` : undefined,
  },
  {
    label: 'Modified at',
    value: user.modified_at ? `${user.modified_at}` : undefined,
  },
  {
    label: 'License Expiry',
    value: user.license_expiry ? `${user.license_expiry}` : undefined,
  },
  {
    label: 'Websocket limit',
    value: user.websocket_limit ? `${user.websocket_limit}` : undefined,
  },
  {
    label: 'Slippage BP',
    value: user.slippage_bp ? `${user.slippage_bp}` : undefined,
  },
];

const getInputFieldsAdmin = (user: User) => {
  return generateUserInfoAdmin(user).map((data, idx) => (
    <TextInputReadOnlyBox label={data.label} value={data.value ?? '-'} key={`user_info_admin_${idx}`} />
  ));
};

export const UserAdminProfileForm: React.FC<Props> = ({ user: userProp, orgData }) => {
  const { isMobile } = useScreenSize();
  const userViewing = useUserContext();
  const navigate = useNavigate();
  const numberOfColumns = isMobile ? 1 : 2;
  const [user, setUser] = useState(userProp);
  const [inputs, setInputs] = useState(getInputFieldsAdmin(userProp));
  const [isEditTradesDialogOpen, setIsEditTradesDialogOpen] = useState(false);
  const [isEditLadderDialogOpen, setIsEditLadderDialogOpen] = useState(false);
  const [isEditTradeAccountsDialogOpen, setIsEditTradeAccountsDialogOpen] = useState(false);
  const [isEditPermissionsDialogOpen, setIsEditPermissionsDialogOpen] = useState(false);
  const [isPrivateAllowed, setIsPrivateAllowed] = useState(false);

  const isReadOnly = checkIsAdminReadOnlyUser(userViewing);

  const canEditTradingLimits = !isReadOnly && checkIsTradingUser(user);

  const canEditTradeAccounts = canEditTradingLimits && orgData;

  const canEditLadderLimits = !isReadOnly && checkIsLadderUser(user);

  const renderEditPermissionsButton = () => {
    if (isReadOnly) return undefined;
    return <EditButton onClick={() => setIsEditPermissionsDialogOpen(true)} />;
  };

  const renderLadderLimitsEditButton = () => {
    if (!canEditLadderLimits) return undefined;
    return <EditButton onClick={() => setIsEditLadderDialogOpen(true)} />;
  };

  const renderTradeAccountsEditButton = () => {
    if (!canEditTradeAccounts) return undefined;
    return <EditButton onClick={() => setIsEditTradeAccountsDialogOpen(true)} />;
  };

  const renderTradeLimitsEditButton = () => {
    if (!canEditTradingLimits) return undefined;

    if ((user.trading_accounts?.length ?? 0) > 0) {
      return <EditButton onClick={() => setIsEditTradesDialogOpen(true)} />;
    }

    if ((user.abn_trad_accounts?.length ?? 0) > 0) {
      return (
        <EditButton
          onClick={() => {
            setIsPrivateAllowed(true);
            setIsEditTradesDialogOpen(true);
          }}
        />
      );
    }

    return undefined;
  };

  const chunks = chunkArray(inputs, numberOfColumns);

  useEffect(() => {
    setInputs(getInputFieldsAdmin(user));
  }, [user]);

  return (
    <>
      <Box style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Paper style={{ padding: 20 }}>
          <MultilineGroup>
            <Typography variant="h4" gutterBottom sx={{ marginBottom: '20px' }}>
              Flux User
            </Typography>
            {chunks.map((chunk, idx) => (
              <InlineGroup key={`group_${idx}`}>{chunk}</InlineGroup>
            ))}
          </MultilineGroup>
        </Paper>
        <Paper style={{ padding: 20 }}>
          <RoundedBox>
            <UserInfoList list={user.permissions} header={'Permissions'} showDivider={true} extraOptions={renderEditPermissionsButton()} />
            <UserInfoList list={user.product_groups} header={'Product Groups'} showDivider={true} />
            <UserInfoList list={user.cot_product_groups} header={'COT Product Groups'} showDivider={true} />
            <UserInfoList list={user.client_type_list} header={'Client type'} showDivider={true} />
            <UserInfoList list={user.snapshot_periods} header={'Snapshot periods'} showDivider={true} />
            <UserInfoList
              list={user.ladder_limits.map(({ symbol, limit }) => `${symbol.toLocaleUpperCase()} : ${limit}`)}
              header={'Ladder Limits'}
              helperText={'Below is formatted as - "Symbol : Ladder Input Limit"'}
              showDivider={true}
              extraOptions={renderLadderLimitsEditButton()}
            />
            {orgData && (
              <UserInfoList
                list={orgData.types ?? []}
                header={`Organization: ${orgData.name}`}
                helperText={'What Flux lets this organization act as. AGENT_ADMIN is only grantable to users of an AGENT organization'}
                showDivider={true}
                extraOptions={
                  <StyledIconButton onClick={() => navigate(`/organizations/${orgData.id}`)} size={'small'}>
                    <i className={'ri-external-link-line'} />
                  </StyledIconButton>
                }
              />
            )}
            {checkIsABNUser(user) && (
              <UserInfoList list={user.abn_trad_accounts.map(elem => elem.toLocaleUpperCase())} header={'ABN Trading Accounts'} showDivider={true} />
            )}
            {!checkIsABNUser(user) && user.organization_id && (
              <UserInfoList
                list={user?.trading_accounts?.map(elem => elem.toLocaleUpperCase()) ?? []}
                header={'Trading Accounts'}
                showDivider={true}
                extraOptions={renderTradeAccountsEditButton()}
              />
            )}
            <UserInfoList
              list={tradeLimitsList(user.trade_limits, true)}
              header={'Trading Limits'}
              helperText={'Below is formatted as - "Symbol : Trading Size Limit : Exchanges : Order Types"'}
              extraOptions={renderTradeLimitsEditButton()}
            />
            <UserFilteredWebsockets user={user} />
          </RoundedBox>
        </Paper>
      </Box>
      <EditTradeLimitsDialog
        user={user}
        isOpen={isEditTradesDialogOpen}
        onClose={() => setIsEditTradesDialogOpen(false)}
        onUserUpdated={user => setUser(user)}
        isPrivateAllowed={isPrivateAllowed}
      />
      <EditLadderLimitsDialog
        user={user}
        isOpen={isEditLadderDialogOpen}
        onClose={() => setIsEditLadderDialogOpen(false)}
        onUserUpdated={user => setUser(user)}
      />
      {orgData && (
        <EditTradeAccountsDialog
          user={user}
          isOpen={isEditTradeAccountsDialogOpen}
          onClose={() => setIsEditTradeAccountsDialogOpen(false)}
          onUserUpdated={user => setUser(user)}
          orgData={orgData}
        />
      )}
      <EditPermissionsDialog
        user={user}
        isOpen={isEditPermissionsDialogOpen}
        onClose={() => setIsEditPermissionsDialogOpen(false)}
        onUserUpdated={user => setUser(user)}
      />
    </>
  );
};
